/**
 * $P point-cloud recogniser core (Vatavu, Anthony & Wobbrock, 2012), with one
 * small extension.
 *
 * A gesture is treated as an unordered cloud of points, so stroke order,
 * stroke direction and the number of strokes don't matter — a "0" drawn
 * clockwise matches one drawn anticlockwise, and a two-stroke "4" matches a
 * one-stroke "4" of the same shape. Aspect ratio is preserved by the
 * normalisation (see geometry.normalizeCloud).
 *
 * Extension (in the spirit of $P+): every point also carries the local
 * orientation of the ink through it, folded to [0, π) so it doesn't depend on
 * which way the stroke was drawn. Two clouds whose points sit in the same
 * places but run in different directions — the vertical stem of a 5 versus
 * the diagonal of a flat-topped 3, a steep "/" versus a leaning "1" — are
 * then told apart. ORIENTATION_WEIGHT = 0 gives plain $P.
 */
import { type Polyline, type Pt, normalizeCloud, resampleStrokes } from './geometry.ts';

/** Points per cloud. 32 is the sweet spot from the $P paper for small alphabets. */
export const CLOUD_SIZE = 32;

/** Weight of the orientation difference (0–1, where 1 = perpendicular) against unit-box distance. */
export const ORIENTATION_WEIGHT = 0.25;

/**
 * Sum of the $P matching weights for CLOUD_SIZE points: Σ (1 − k/n) = (n + 1) / 2.
 * Dividing a cloud distance by this gives the weighted mean point distance in
 * unit-box units, which is what the similarity mapping in classify.ts expects.
 */
export const WEIGHT_SUM = (CLOUD_SIZE + 1) / 2;

export interface CloudPoint extends Pt {
  /** Local ink orientation in [0, π), or −1 when undefined (a dot). */
  o: number;
}

export type Cloud = readonly CloudPoint[];

/** Orientation of the ink at each resampled point (central differences, one-sided at the ends). */
function orientations(line: readonly Pt[]): number[] {
  return line.map((_, i) => {
    const a = line[Math.max(0, i - 1)]!;
    const b = line[Math.min(line.length - 1, i + 1)]!;
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    if (dx === 0 && dy === 0) return -1;
    const o = Math.atan2(dy, dx);
    return o < 0 ? o + Math.PI : o >= Math.PI ? o - Math.PI : o;
  });
}

/** Resample to CLOUD_SIZE points, attach orientations, scale-normalise and centre. Null for empty input. */
export function toCloud(strokes: readonly Polyline[]): Cloud | null {
  const lines = resampleStrokes(strokes, CLOUD_SIZE);
  const pts = lines.flat();
  if (pts.length !== CLOUD_SIZE) return null;
  const o = lines.flatMap(orientations);
  return normalizeCloud(pts).map((p, i) => ({ x: p.x, y: p.y, o: o[i]! }));
}

/** Folded orientation difference scaled to 0–1 (0.5 when either is undefined). */
function orientationGap(a: number, b: number): number {
  if (a < 0 || b < 0) return 0.5;
  const d = Math.abs(a - b);
  return Math.min(d, Math.PI - d) / (Math.PI / 2);
}

/**
 * Greedy matching of cloud `a` onto cloud `b`, starting at `a[start]`.
 * Each point of `a` takes its nearest still-unmatched point of `b`; earlier
 * matches get a higher weight (they had more choice). Stops early once the
 * running sum exceeds `bound` (it can then only be worse than a known match).
 */
function cloudDistance(a: Cloud, b: Cloud, start: number, bound: number): number {
  const n = a.length;
  const matched = new Uint8Array(n);
  const w2 = ORIENTATION_WEIGHT * ORIENTATION_WEIGHT;
  let sum = 0;
  let i = start;
  let k = 0;
  do {
    const p = a[i]!;
    let best = Infinity;
    let bestJ = 0;
    for (let j = 0; j < n; j++) {
      if (matched[j]) continue;
      const q = b[j]!;
      const dx = p.x - q.x;
      const dy = p.y - q.y;
      let d = dx * dx + dy * dy;
      if (d >= best) continue;
      const g = orientationGap(p.o, q.o);
      d += w2 * g * g;
      if (d < best) {
        best = d;
        bestJ = j;
      }
    }
    matched[bestJ] = 1;
    sum += (1 - k / n) * Math.sqrt(best);
    if (sum >= bound) return sum;
    k++;
    i = (i + 1) % n;
  } while (i !== start);
  return sum;
}

/**
 * $P distance between two clouds of equal size: the best greedy match over
 * ~√n starting points, in both directions. Lower is better; 0 = identical.
 */
export function cloudMatch(a: Cloud, b: Cloud, bound = Infinity): number {
  const n = a.length;
  const step = Math.max(1, Math.floor(Math.sqrt(n)));
  let min = bound;
  for (let i = 0; i < n; i += step) {
    min = Math.min(min, cloudDistance(a, b, i, min), cloudDistance(b, a, i, min));
  }
  return min;
}
